import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ShoppingBag, ArrowLeft, Plus, Minus, ShoppingCart, 
  Check, Truck, Shield, Tag, ChevronLeft, ChevronRight 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return "https://placehold.co/600x600/e2e8f0/64748b?text=Producto";
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const formatPrice = (price) => {
  return `L ${price.toLocaleString("es-HN", { minimumFractionDigits: 2 })}`;
};

// Simple cart hook - shares state with TiendaPage via localStorage
const useCart = () => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("shop_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("shop_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  };

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return { cart, addToCart, itemCount };
};

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart, itemCount } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/public/shop/products/${slug}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity);
    toast.success(`${product.name} agregado al carrito`);
  };

  const images = product?.images?.length > 0 
    ? product.images 
    : [product?.featured_image_url];

  const hasDiscount = product?.compare_price && product?.compare_price > product?.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-medium text-gray-600 mb-2">Producto no encontrado</h2>
          <Link to="/tienda">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a la tienda
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <section className="py-4 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link 
              to="/tienda" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
              data-testid="back-to-store"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a la tienda
            </Link>
            <Link 
              to="/tienda" 
              className="relative"
              data-testid="cart-link"
            >
              <ShoppingCart className="h-6 w-6 text-gray-600" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </section>

      {/* Product Detail */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="relative aspect-square bg-white rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={getImageUrl(images[selectedImage])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
                {hasDiscount && (
                  <Badge className="absolute top-4 left-4 bg-red-500 text-white text-lg px-3 py-1">
                    -{discountPercent}%
                  </Badge>
                )}
                {product.is_featured && (
                  <Badge className="absolute top-4 right-4 bg-amber-500 text-white">
                    Destacado
                  </Badge>
                )}
                {product.stock <= 0 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">Agotado</span>
                  </div>
                )}

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={() => setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? "border-primary" : "border-transparent"
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Category */}
              {product.category && (
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span className="text-sm text-primary font-medium">{product.category}</span>
                </div>
              )}

              {/* Title */}
              <h1 
                className="font-heading text-3xl md:text-4xl font-bold text-brand-text"
                data-testid="product-title"
              >
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-3">
                <span 
                  className="text-3xl font-bold text-primary"
                  data-testid="product-price"
                >
                  {formatPrice(product.price)}
                </span>
                {hasDiscount && (
                  <span className="text-xl text-gray-400 line-through">
                    {formatPrice(product.compare_price)}
                  </span>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div 
                  className="text-brand-textSecondary prose max-w-none"
                  data-testid="product-description"
                >
                  <p>{product.description}</p>
                </div>
              )}

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                {product.stock > 0 ? (
                  <>
                    <Check className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 font-medium">
                      En stock ({product.stock} disponibles)
                    </span>
                  </>
                ) : (
                  <span className="text-red-500 font-medium">Agotado</span>
                )}
              </div>

              {/* Quantity & Add to Cart */}
              {product.stock > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  {/* Quantity Selector */}
                  <div className="flex items-center gap-4">
                    <span className="text-brand-text font-medium">Cantidad:</span>
                    <div className="flex items-center gap-2 bg-gray-100 rounded-full px-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        data-testid="quantity-decrease"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span 
                        className="w-8 text-center font-medium"
                        data-testid="quantity-value"
                      >
                        {quantity}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full"
                        onClick={() => setQuantity((q) => Math.min(product.stock, q + 1))}
                        data-testid="quantity-increase"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <Button 
                    size="lg" 
                    className="w-full rounded-full text-lg h-14"
                    onClick={handleAddToCart}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Agregar al Carrito - {formatPrice(product.price * quantity)}
                  </Button>

                  {/* Direct Checkout */}
                  <Link to="/tienda/checkout" onClick={handleAddToCart}>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="w-full rounded-full"
                      data-testid="buy-now-btn"
                    >
                      Comprar Ahora
                    </Button>
                  </Link>
                </div>
              )}

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t">
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                  <Truck className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-medium text-sm">Envío Local</p>
                    <p className="text-xs text-gray-500">A coordinar</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl">
                  <Shield className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-medium text-sm">Pago Seguro</p>
                    <p className="text-xs text-gray-500">Transferencia</p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Forma de Pago:</strong> Aceptamos efectivo, transferencia bancaria y pago móvil. 
                  Nos comunicaremos contigo para coordinar el pago y la entrega.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
